import React, { useState } from 'react';
import { RefreshCw, Loader2, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import Icon from '../../../components/AppIcon';


// Reusable Loading State Components
const LoadingSpinner = ({ size = 'md', color = 'orange' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-4',
    lg: 'w-12 h-12 border-4',
    xl: 'w-16 h-16 border-[6px]'
  };

  const colorClasses = {
    orange: 'spinner-orange',
    green: 'spinner-green'
  };

  return (
    <div 
      className={`inline-block rounded-full ${sizeClasses?.[size]} ${colorClasses?.[color]}`}
      role="status"
      aria-live="polite"
      aria-label="Loading"
    />
  );
};

const SkeletonLoader = ({ className = '', rows = 3 }) => (
  <div className={`space-y-3 ${className}`} aria-busy="true" aria-live="polite">
    {[...Array(rows)]?.map((_, i) => (
      <div key={i} className="skeleton h-4 w-full" />
    ))}
  </div>
);

const TableSkeletonLoader = ({ columns = 4, rows = 5 }) => (
  <div className="overflow-hidden rounded-sm border border-border" aria-busy="true">
    <table className="w-full">
      <thead className="bg-surface-light">
        <tr>
          {[...Array(columns)]?.map((_, i) => (
            <th key={i} className="px-4 py-3">
              <div className="skeleton h-4 w-full" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {[...Array(rows)]?.map((_, rowIndex) => (
          <tr key={rowIndex}>
            {[...Array(columns)]?.map((_, colIndex) => (
              <td key={colIndex} className="px-4 py-3">
                <div className="skeleton h-4 w-full" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ProgressBar = ({ progress = 0, color = 'green', label = '' }) => (
  <div className="w-full" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
    {label && (
      <div className="flex justify-between mb-2 text-sm font-medium text-text-secondary">
        <span>{label}</span>
        <span>{progress}%</span>
      </div>
    )}
    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
      <div
        className={`h-full transition-all duration-300 ${
          color === 'green' ? 'bg-secondary' : 'bg-primary'
        }`}
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
);

const ButtonLoadingState = ({ loading = false, onClick, children, variant = 'primary' }) => {
  const baseClasses = 'px-6 py-2.5 rounded-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2';
  
  const variantClasses = {
    primary: 'btn-primary ripple-effect',
    secondary: 'btn-secondary ripple-effect',
    outline: 'btn-outline'
  };

  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`${baseClasses} ${variantClasses?.[variant]}`}
      aria-busy={loading}
    >
      {loading && <LoadingSpinner size="sm" color={variant === 'secondary' ? 'green' : 'orange'} />}
      {loading ? 'Processing...' : children}
    </button>
  );
};

const CardLoadingState = () => (
  <div className="card p-6 space-y-4" aria-busy="true">
    <div className="flex items-center gap-4">
      <div className="skeleton w-12 h-12 rounded-full" />
      <div className="flex-1 space-y-2">
        <div className="skeleton h-4 w-3/4" />
        <div className="skeleton h-3 w-1/2" />
      </div>
    </div>
    <SkeletonLoader rows={3} />
  </div>
);

const ModalLoadingOverlay = ({ message = 'Loading...', progress = null }) => (
  <div className="modal-overlay flex items-center justify-center z-50">
    <div className="modal-content bg-surface rounded-sm p-8 shadow-xl max-w-md w-full mx-4">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <LoadingSpinner size="xl" color="orange" />
        </div>
        <p className="text-lg font-medium text-text-primary">{message}</p>
        {progress !== null && (
          <ProgressBar progress={progress} color="green" />
        )}
      </div>
    </div>
  </div>
);

const LiveStatusIndicator = ({ status = 'loading', label = '' }) => {
  const statusConfig = {
    loading: { color: 'bg-primary', icon: Loader2, text: 'In Progress', animate: 'animate-spin' },
    complete: { color: 'bg-secondary', icon: CheckCircle2, text: 'Complete', animate: '' },
    error: { color: 'bg-error', icon: AlertCircle, text: 'Failed', animate: '' }
  };

  const config = statusConfig?.[status] || statusConfig?.loading;
  const Icon = config?.icon;

  return (
    <div className="flex items-center gap-2" role="status" aria-live="polite">
      <span className={`w-2 h-2 rounded-full ${config?.color} ${status === 'loading' ? 'pulse-animation' : ''}`} />
      <Icon className={`w-4 h-4 ${config?.color?.replace('bg-', 'text-')} ${config?.animate}`} />
      <span className="text-sm font-medium text-text-secondary">
        {label || config?.text}
      </span>
    </div>
  );
};

// Main Demo Page
const GlobalLoadingStates = () => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showModalLoading, setShowModalLoading] = useState(false);
  const [realTimeStatus, setRealTimeStatus] = useState('loading');

  const handleButtonClick = () => {
    setButtonLoading(true);
    setTimeout(() => setButtonLoading(false), 3000);
  };

  const simulateUpload = () => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 300);
  };

  const simulateRealTimeOperation = () => {
    setRealTimeStatus('loading');
    setTimeout(() => {
      setRealTimeStatus('complete');
      setTimeout(() => setRealTimeStatus('loading'), 2000);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-2xl font-bold text-text-primary">Global Loading States</h1>
          <p className="text-sm text-text-secondary mt-1">
            Consistent loading feedback with orange/green theme integration
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Spinner Variants */}
          <section className="card p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Loading Spinners</h2>
            <div className="flex flex-wrap items-center gap-8">
              <div className="text-center">
                <LoadingSpinner size="sm" color="orange" />
                <p className="text-xs text-text-secondary mt-2">Small Orange</p>
              </div>
              <div className="text-center">
                <LoadingSpinner size="md" color="orange" />
                <p className="text-xs text-text-secondary mt-2">Medium Orange</p>
              </div>
              <div className="text-center">
                <LoadingSpinner size="lg" color="green" />
                <p className="text-xs text-text-secondary mt-2">Large Green</p>
              </div>
              <div className="text-center">
                <LoadingSpinner size="xl" color="green" />
                <p className="text-xs text-text-secondary mt-2">XL Green</p>
              </div>
            </div>
          </section>

          {/* Skeleton Loaders */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary">Skeleton Screens</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <CardLoadingState />
              <CardLoadingState />
            </div>
          </section>

          {/* Table Loading */}
          <section className="card p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Table Loading State</h2>
            <TableSkeletonLoader columns={5} rows={4} />
          </section>

          {/* Progress Bars */}
          <section className="card p-6 space-y-6">
            <h2 className="text-xl font-semibold text-text-primary">Progress Indicators</h2>
            
            <div>
              <ProgressBar progress={uploadProgress} color="green" label="File Upload" />
              <button
                onClick={simulateUpload}
                className="btn-secondary mt-3 px-4 py-2 text-sm"
              >
                <Upload className="w-4 h-4 inline mr-2" />
                Simulate Upload
              </button>
            </div>

            <div>
              <ProgressBar progress={75} color="orange" label="Processing Data" />
            </div>
          </section>

          {/* Button Loading States */}
          <section className="card p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Button Loading States</h2>
            <div className="flex flex-wrap gap-4">
              <ButtonLoadingState
                loading={buttonLoading}
                onClick={handleButtonClick}
                variant="primary"
              >
                Submit Form
              </ButtonLoadingState>

              <ButtonLoadingState
                loading={buttonLoading}
                onClick={handleButtonClick}
                variant="secondary"
              >
                Save Changes
              </ButtonLoadingState>

              <ButtonLoadingState
                loading={buttonLoading}
                onClick={handleButtonClick}
                variant="outline"
              >
                Export Data
              </ButtonLoadingState>
            </div>
          </section>

          {/* Real-Time Status */}
          <section className="card p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Live Status Updates</h2>
            <div className="flex items-center gap-6">
              <LiveStatusIndicator status={realTimeStatus} label="Operation Status" />
              <button
                onClick={simulateRealTimeOperation}
                className="btn-outline px-4 py-2 text-sm"
              >
                <RefreshCw className="w-4 h-4 inline mr-2" />
                Simulate Operation
              </button>
            </div>
          </section>

          {/* Modal Loading Demo */}
          <section className="card p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Modal Loading Overlay</h2>
            <button
              onClick={() => {
                setShowModalLoading(true);
                setTimeout(() => setShowModalLoading(false), 3000);
              }}
              className="btn-primary px-6 py-2.5"
            >
              Show Modal Loading
            </button>
          </section>

          {/* Accessibility Features */}
          <section className="card p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Accessibility Features</h2>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5" />
                <span>ARIA live regions for screen reader announcements</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5" />
                <span>Reduced motion preferences respected in animations</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5" />
                <span>Proper role and aria-busy attributes on loading elements</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5" />
                <span>Loading timeouts prevent infinite states</span>
              </li>
            </ul>
          </section>
        </div>
      </main>

      {/* Modal Loading Overlay */}
      {showModalLoading && (
        <ModalLoadingOverlay message="Processing your request..." progress={null} />
      )}
    </div>
  );
};

export default GlobalLoadingStates;